import { Box, Dialog, DialogContent, Theme, Typography } from "@mui/material";
import { SearchIcon } from "../../../customSVGs";
import { useEffect, useState } from "react";
import CustomTable from "../../common/customTable";
import { SwitchTableBody,SwitchTableBodyResponsive } from "../common/Table";
import { GetSchemeDataResponseType } from "../../../../redux-store/types/investmentdetails";
import { getSchemeData } from "../../../../redux-store/actions/investmentDetails";
import { useDispatch } from "react-redux";
import { HoldingsTableData } from "../holdings/types";
import { useDebounce } from "../../../../utils/useDebounce";
import { TableHeaderData } from "../../types";

export const SwitchTableHead: TableHeaderData[] = [
    {
        id: "schemeName",
        label: "SCHEME",
        align: "left",
        isSortable: true
    },
    {
        id: "year1Return",
        label: "1Y Return",
        align: "center",
        isSortable: true
    },
    {
        id: "year3Return",
        label: "3Y Return",
        align: "center",
        isSortable: true
    },
    {
        id: "year5Return",
        label: "5Y Returns",
        align: "center",
        isSortable: true
    },
]

export default function FromSchemeDialog({
    open,
    onClose,
    isSwitchScreen,
    outgoingSchDetails,
    clientCode,
    handleProcess
}: {
    open: boolean;
    onClose: () => void;
    isSwitchScreen: boolean;
    outgoingSchDetails: HoldingsTableData;
    clientCode: string;
    handleProcess: (schemeId: string, divReinvestAllowed?: any) => void
}): JSX.Element {
    const dispatch = useDispatch()

    const [searchText, setSearchText] = useState<string>('')
    const [tableLoading, setTableLoading] = useState(false)
    const [schemeTableData, setSchemeTableData] = useState<GetSchemeDataResponseType>({} as GetSchemeDataResponseType)
    const [selectedScheme, setSelectedScheme] = useState<string>('');
    const [sortObj, setSortObj] = useState<{ orderBy: string, order: 'asc' | 'desc' }>({ orderBy: "schemeName", order: "asc" })
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const debounceSearchText = useDebounce(searchText, 500);

    const getScheme = async (
        pages = 1,
        size = 10,
        sort = "schemeName",
        order = "asc",
        search = ["1"],
        initialLoad = false,
        nameSearch = ""
    ) => {
        try {
            setTableLoading(true)

            const payload: any = {
                sort: sort,
                order: order,
                currentPage: pages,
                fundsPerPage: size,
                amcId: search[0],
                category: '',
                searchInput: nameSearch
            }
            const res = (await dispatch(getSchemeData(payload)) as unknown) as GetSchemeDataResponseType

            setSchemeTableData(res)

            initialLoad && setSelectedScheme(
                res?.currentPageFunds[0]?.scheme === outgoingSchDetails?.schemeId ?
                    res?.currentPageFunds[1]?.scheme :
                    res?.currentPageFunds[0]?.scheme
            )

        } catch (error) {
            console.log("Error:  ", error)
        } finally {
            setTableLoading(false)
        }
    }

    useEffect(() => {
        const fetchAll = async () => {
            await getScheme(
                1,
                10,
                "schemeName",
                "asc",
                [outgoingSchDetails.amcId ?? ""],
                true,
                debounceSearchText
            )
        }
        if (outgoingSchDetails?.amcId && clientCode) fetchAll()
    }, [clientCode, debounceSearchText])


    const handleSchemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedScheme(event.target.value)
    }

    const handleSort = (orderBy: string, order: 'asc' | 'desc') => {
        setSortObj({ orderBy, order })
        getScheme(page, 10, orderBy, order, [outgoingSchDetails?.amcId ?? ""], false, debounceSearchText)
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(event.target.value))
        getScheme(page, Number(event.target.value), sortObj.orderBy, sortObj.order, [outgoingSchDetails?.amcId ?? ""], false, debounceSearchText)
    }

    const handlePageChange = (event:
        React.ChangeEvent<unknown>, value: number) => {
        setPage(value + 1)
        getScheme(value + 1, rowsPerPage, sortObj.orderBy, sortObj.order, [outgoingSchDetails?.amcId ?? ""], false, debounceSearchText)
    }

    return (
        <Dialog
            onClose={onClose}
            open={open}
            maxWidth='lg'
            fullWidth
            sx={(theme: Theme) => ({
                '& .MuiPaper-root': {
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    padding: {xs:"10px",sm:"30px"},
                    width: "1063px",
                    height: "80%"
                },
            })}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "20px"
                }}
            >
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "32.5px",
                        color: theme.palette.text.primary
                    })}
                >
                    {isSwitchScreen ? 'Switch' : 'Transfer'} To
                </Typography>
            </Box>
            <Box sx={(theme: Theme) => ({
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                border: `1px solid ${theme.palette.divider}`,
                padding: '15px 14px',
                borderRadius: '57px',
            })}
            >
                <SearchIcon />
                <Box
                    component={'input'}
                    placeholder='Search'
                    onChange={(e) => setSearchText(e.target.value)}
                    inputMode='text'
                    sx={(theme: Theme) => ({
                        border: 'none',
                        background: 'transparent',
                        width: '100%',
                        fontSize: '16px',
                        fontFamily: theme.typography.fontFamily,
                        color: theme.palette.text.primary,
                        '&:focus': {
                            outline: 'none',
                            border: 'none',
                        }
                    })}
                />
            </Box>
            <DialogContent
                sx={{
                    maxHeight: "100%",
                    overflowY: "auto",
                    padding: 0,
                    marginTop: "30px"
                }}
            >
                <Box>
                    <CustomTable
                        loadingRowsNum={10}
                        isLoading={tableLoading}
                        tableBody={
                            <SwitchTableBody
                                currentData={{
                                    itemData: outgoingSchDetails
                                }}
                                handleProcess={handleProcess}
                                tableBodyData={schemeTableData?.currentPageFunds || []}
                                handleSchemeChange={handleSchemeChange}
                                selectedScheme={selectedScheme}
                                isSwitchScreen={isSwitchScreen}
                            />
                        }
                        tableData={schemeTableData?.currentPageFunds || []}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeaderData={SwitchTableHead}
                        tableHeadRowCustomStyles={{
                            '& th:first-of-type': {
                                paddingLeft: "0px"
                            }
                        }}
                        handleSort={handleSort}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        tableDataCount={schemeTableData?.totalLength || 0}
                        isPaginationVisible={true}
                        order={sortObj.order}
                        orderBy={sortObj.orderBy}
                        handleRowsPerPageChange={handleTableRowsPerPageChange as any}
                        rowsPerPage={rowsPerPage}
                        handlePageChange={handlePageChange as any}
                        currentPage={page - 1}
                        manualResponsiveBody={true}
                        hideResponsiveHeader={true}
                        ManualResponsiveBodyComponent={
                            <SwitchTableBodyResponsive
                                currentData={{
                                    itemData: outgoingSchDetails
                                }}
                                handleProcess={handleProcess}
                                tableBodyData={schemeTableData?.currentPageFunds || []}
                                handleSchemeChange={handleSchemeChange}
                                selectedScheme={selectedScheme}
                                isSwitchScreen={isSwitchScreen} tableHeaderData={[]}                            />
                        }
                    />
                </Box>
            </DialogContent>
        </Dialog >
    )
}